// Pure "is this announcement active today" logic per recurrence_type — kept
// separate from the sheet-shape decoration so both AnnouncementBanner and
// the Announcements page can share the same rules rather than drifting.
import { daysUntilAnnualDate } from './annualDates';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// { isActiveToday, daysUntil } for one announcement on `todayDateStr`
// ("YYYY-MM-DD"). daysUntil is 0 on the actual occurrence day; for one_time
// it's always 0 while active, since there's no lead-up — it just shows
// immediately until end_date (its expiration). Named isActiveToday (not
// `active`) specifically to avoid colliding with the sheet's own `active`
// admin on/off toggle column when the two get merged together. Doesn't
// consider that toggle itself — see isAnnouncementActive for the combined
// check callers should actually use.
export function announcementStatus(a, todayDateStr) {
  if (a.recurrence_type === 'one_time') {
    if (!a.end_date) return { isActiveToday: false, daysUntil: null };
    const isActiveToday = todayDateStr <= a.end_date;
    return { isActiveToday, daysUntil: isActiveToday ? 0 : null };
  }

  if (a.recurrence_type === 'annual') {
    // Stored 1-12 (human-friendly, matches the create/edit form) —
    // annualDates.js's month math is 0-11 (JS Date convention), same as
    // birthdays.js's parseBirthday.
    const month = Number(a.month) - 1;
    const day = Number(a.day);
    const daysUntil = daysUntilAnnualDate(month, day, todayDateStr);
    const leadDays = Number(a.lead_days) || 0;
    return { isActiveToday: daysUntil <= leadDays, daysUntil };
  }

  if (a.recurrence_type === 'interval') {
    if (!a.start_date) return { isActiveToday: false, daysUntil: null };
    const start = new Date(`${a.start_date}T00:00:00.000Z`);
    const today = new Date(`${todayDateStr}T00:00:00.000Z`);
    const intervalDays = Number(a.interval_days) || 1;
    const leadDays = Number(a.lead_days) || 0;
    const daysSinceStart = Math.round((today - start) / MS_PER_DAY);
    if (daysSinceStart < 0) return { isActiveToday: false, daysUntil: null }; // hasn't started yet
    const daysIntoCycle = daysSinceStart % intervalDays;
    const daysUntil = daysIntoCycle === 0 ? 0 : intervalDays - daysIntoCycle;
    return { isActiveToday: daysUntil <= leadDays, daysUntil };
  }

  return { isActiveToday: false, daysUntil: null };
}

// The one check callers should actually use — combines the admin's on/off
// toggle with the date-based recurrence rule above. An announcement past
// its window never shows regardless of `active`, and a deactivated one
// never shows regardless of the date.
export function isAnnouncementActive(a, todayDateStr) {
  return a.active !== false && announcementStatus(a, todayDateStr).isActiveToday;
}

// Every announcement whose combined check passes today, decorated with its
// daysUntil and sorted soonest (already-active, daysUntil 0) first.
export function activeAnnouncements(announcements, todayDateStr) {
  return announcements
    .map((a) => ({ ...a, ...announcementStatus(a, todayDateStr) }))
    .filter((a) => a.active !== false && a.isActiveToday)
    .sort((x, y) => (x.daysUntil || 0) - (y.daysUntil || 0));
}

// Short human label for the recurrence schedule — used on the Announcements
// page cards, e.g. "Until Dec 26", "Annual", "Repeats every 2 weeks".
export function formatRecurrenceLabel(a) {
  if (a.recurrence_type === 'one_time') {
    if (!a.end_date) return '';
    const d = new Date(`${a.end_date}T00:00:00.000Z`);
    return `Until ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
  }
  if (a.recurrence_type === 'annual') {
    return 'Annual';
  }
  if (a.recurrence_type === 'interval') {
    const days = Number(a.interval_days);
    if (days === 7) return 'Repeats weekly';
    if (days === 14) return 'Repeats every 2 weeks';
    if (days % 7 === 0) return `Repeats every ${days / 7} weeks`;
    return `Repeats every ${days} day${days === 1 ? '' : 's'}`;
  }
  return '';
}

// The plain date a VA sees on a card — "Until Dec 26" for a one-time
// announcement (they still need to know how long it's relevant), or the
// actual occurrence date for annual/interval (e.g. "Sep 15") rather than
// the admin-facing schedule jargon ("Annual", "Repeats every 2 weeks") that
// formatRecurrenceLabel shows instead.
export function formatVaDateLabel(a, todayDateStr) {
  if (a.recurrence_type === 'one_time') {
    if (!a.end_date) return '';
    const d = new Date(`${a.end_date}T00:00:00.000Z`);
    return `Until ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
  }
  const { daysUntil } = announcementStatus(a, todayDateStr);
  if (daysUntil == null) return '';
  const today = new Date(`${todayDateStr}T00:00:00.000Z`);
  const occurrence = new Date(today.getTime() + daysUntil * MS_PER_DAY);
  return occurrence.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Per-browser "have I visited the Announcements page" watermark, backing
// the Sidebar's unread badge — a single timestamp rather than per-item read
// receipts, so it doesn't need any server-side storage. Doesn't sync across
// devices for the same person by design (localStorage is per-browser), a
// tradeoff accepted in favor of zero backend work for this.
export const ANNOUNCEMENTS_SEEN_KEY = 'trackwithtux_announcements_last_seen';

export function getAnnouncementsLastSeen() {
  try {
    return localStorage.getItem(ANNOUNCEMENTS_SEEN_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function markAnnouncementsSeen() {
  try {
    localStorage.setItem(ANNOUNCEMENTS_SEEN_KEY, new Date().toISOString());
  } catch (e) {
    // Private browsing / storage disabled — worst case the badge just
    // doesn't clear, not worth failing the page visit over.
  }
}

// Count of currently-active announcements created since the last visit —
// the Sidebar badge's number, matching the Leaves pending-count badge style.
export function countUnseenAnnouncements(announcements, todayDateStr) {
  const lastSeen = getAnnouncementsLastSeen();
  return activeAnnouncements(announcements, todayDateStr).filter((a) => a.created_at > lastSeen).length;
}
